import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { formatApiError } from "@/lib/utils";
import { CostRateCard, DiamondRateCard } from "@/pages/WholesalerDetail";
import { SellingRatesSheet } from "@/pages/WholesalerPortal";

export const CURRENCIES = ["INR", "USD", "GBP", "EUR"];

// CS 22.14 -- step 1: name + currency (creates the price list with wholesaler defaults).
export function NewPriceListDialog({ open, onOpenChange, onCreated }) {
  const [name, setName] = useState("");
  const [currency, setCurrency] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) { setName(""); setCurrency(""); setErr(""); } }, [open]);
  const save = async () => {
    if (!name.trim()) return setErr("A name is required.");
    setBusy(true); setErr("");
    try {
      const { data } = await api.post("/wholesale-price-lists", { name: name.trim(), currency });
      toast.success(`Price list "${data.name}" created. Fill in the chart.`);
      onCreated(data);
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || "Could not create price list");
    } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white" data-testid="mc-pl-new-dialog">
        <DialogHeader><DialogTitle className="font-serif text-2xl">Add new custom pricing</DialogTitle></DialogHeader>
        <DialogDescription>Pick the currency first, then fill in the same cost and diamond charts used for wholesalers.</DialogDescription>
        <div className="space-y-3">
          <Select value={currency} onValueChange={setCurrency}>
            <SelectTrigger data-testid="mc-pl-currency-select"><SelectValue placeholder="Currency" /></SelectTrigger>
            <SelectContent>{CURRENCIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
          </Select>
          <Input value={name} onChange={(e) => { setName(e.target.value); setErr(""); }} placeholder="Price list name" data-testid="mc-pl-name-input" />
          {err && <p className="text-xs text-red-700" data-testid="mc-pl-error">{err}</p>}
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={save} disabled={busy || !currency || !name.trim()} className="bg-[#0D1B2A] text-white" data-testid="mc-pl-create-submit">Save &amp; fill chart</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// Step 2 / edit: the exact wholesaler chart components, pointed at the price list.
export function PriceListChartDialog({ pl, onClose }) {
  const [tab, setTab] = useState("cost");
  const [sellOpen, setSellOpen] = useState(false);
  const [selling, setSelling] = useState(null);
  const sellUrl = pl ? `/wholesale-price-lists/${pl.id}/selling-settings` : "";
  useEffect(() => { if (pl) { setTab("cost"); api.get(sellUrl).then((r) => setSelling(r.data)); } }, [pl, sellUrl]);
  if (!pl) return null;
  return (
    <Dialog open={!!pl} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="bg-white max-w-6xl w-[96vw] max-h-[92vh] overflow-y-auto overflow-x-hidden" data-testid="mc-pl-chart-dialog">
        <DialogHeader className="min-w-0">
          <DialogTitle className="font-serif text-2xl break-words">{pl.name} <span className="text-sm text-slate-500 font-sans">· {pl.currency}</span></DialogTitle>
        </DialogHeader>
        <DialogDescription className="min-w-0">Wholesaler Cost uses the cost card and the cost side of the diamond chart. B2B price uses the selling card (save and lock it to show B2B prices).</DialogDescription>
        <Tabs value={tab} onValueChange={setTab} className="space-y-4 min-w-0">
          <TabsList className="bg-[#0D1B2A]/5 flex-wrap h-auto">
            <TabsTrigger value="cost" data-testid="mc-pl-tab-cost">Cost Rate-Card</TabsTrigger>
            <TabsTrigger value="diamond" data-testid="mc-pl-tab-diamond">Diamond Rate-Card</TabsTrigger>
            <TabsTrigger value="selling" data-testid="mc-pl-tab-selling">B2B Selling</TabsTrigger>
          </TabsList>
          <TabsContent value="cost" className="min-w-0 overflow-x-auto"><CostRateCard wid={pl.id} canEdit onSaved={() => {}} currency={pl.currency} /></TabsContent>
          <TabsContent value="diamond" className="min-w-0 overflow-x-auto"><DiamondRateCard wid={pl.id} canEdit currency={pl.currency} /></TabsContent>
          <TabsContent value="selling">
            <div className="space-y-3" data-testid="mc-pl-selling-panel">
              <p className="text-sm text-slate-600">
                Selling rates are {selling?.selling_rates_locked_in ? <b className="text-emerald-700">locked in</b> : <b className="text-amber-700">not locked yet</b>}.
              </p>
              <Button onClick={() => setSellOpen(true)} className="bg-[#0D1B2A] text-white" data-testid="mc-pl-open-selling">Edit B2B selling rates</Button>
            </div>
          </TabsContent>
        </Tabs>
        <div className="flex justify-end pt-2"><Button variant="outline" onClick={onClose} data-testid="mc-pl-chart-done">Done</Button></div>
        <SellingRatesSheet open={sellOpen} onOpenChange={setSellOpen} settings={selling} currency={pl.currency}
          onSaved={(d) => setSelling(d)}
          endpoints={{ client: api, opts: {}, ratecard: `/wholesalers/${pl.id}/diamond-ratecard`, selling: sellUrl }} />
      </DialogContent>
    </Dialog>
  );
}

export function CustomPriceLists({ refreshKey }) {
  const [items, setItems] = useState([]);
  const [edit, setEdit] = useState(null);
  const [newOpen, setNewOpen] = useState(false);
  const load = () => api.get("/wholesale-price-lists").then((r) => setItems(r.data.items));
  useEffect(() => { load(); }, [refreshKey]);
  const rename = async (pl) => {
    const name = window.prompt("New name", pl.name);
    if (name === null) return;
    try { await api.patch(`/wholesale-price-lists/${pl.id}`, { name }); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Rename failed"); }
  };
  const remove = async (pl) => {
    if (!window.confirm(`Delete price list "${pl.name}"? Catalog links using it keep their saved copy.`)) return;
    try { await api.delete(`/wholesale-price-lists/${pl.id}`); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Delete failed"); }
  };
  return (
    <div className="luxe-card p-5 space-y-3" data-testid="mc-price-lists">
      <div className="flex items-center justify-between">
        <h2 className="font-serif text-xl text-[#0D1B2A]">Custom price lists</h2>
        <Button size="sm" variant="outline" onClick={() => setNewOpen(true)} data-testid="mc-pl-add-btn">Add new custom pricing</Button>
      </div>
      {items.length === 0 && <div className="text-sm text-slate-400" data-testid="mc-pl-empty">No custom price lists yet.</div>}
      {items.map((pl) => (
        <div key={pl.id} className="flex items-center gap-3 border-t border-[#0D1B2A]/5 pt-2 text-sm" data-testid={`mc-pl-row-${pl.id}`}>
          <span className="font-medium flex-1">{pl.name}</span>
          <span className="text-xs text-slate-500">{pl.currency}</span>
          <button className="text-xs underline" onClick={() => setEdit(pl)} data-testid={`mc-pl-edit-${pl.id}`}>Edit chart</button>
          <button className="text-xs underline" onClick={() => rename(pl)} data-testid={`mc-pl-rename-${pl.id}`}>Rename</button>
          <button className="text-xs underline text-red-700" onClick={() => remove(pl)} data-testid={`mc-pl-delete-${pl.id}`}>Delete</button>
        </div>
      ))}
      <NewPriceListDialog open={newOpen} onOpenChange={setNewOpen} onCreated={(pl) => { setNewOpen(false); load(); setEdit(pl); }} />
      <PriceListChartDialog pl={edit} onClose={() => setEdit(null)} />
    </div>
  );
}
